// 封装的localStorage
import env from 'root/blackcard/util/env';
import { zzcCall } from 'root/common/nativeApi';
import { getAppVersion, compareVersion } from 'root/common/util/getAppVersion';
import keyConfig, { keys } from '../const/localStorageKey';

/**
 * @typedef {Object} setOption
 * @prop {number|Date} [expires] 过期时间或者过期时长，如果传入number，则按秒作为单位，如果传入Date类型，则作为过期时间直接使用
 * @prop {string} [namespace] 传递给app端的命名空间
 * @prop {boolean} [bare] 当这个选项为true，则不处理存入的数据，此时只接受字符串，取的时候也需要传入此参数
 *
 * @typedef {Object} getOption
 * @prop {boolean} [bare] 当这个选项为true时，不处理取出的数据，使用此字段时，expires会失效
 * @prop {string} [namespace] 传递给app端的命名空间
 */

const baseVersion = '1.20.0'; // 原生存储最低可用版本
// 原生存储是否可用
const available = env.isApp && compareVersion(getAppVersion(), baseVersion) === 1;

let deviceready = false;
/**
 * @type {[Function]}
 */
let queue = [];

document.addEventListener(
    'deviceready',
    function() {
        deviceready = true;
        queue.forEach(fun => {
            try {
                fun();
            } catch (error) {
                console.error(error);
            }
        });
        queue = [];
    },
    false
);

const defaultOption = {};
const defaultGetOption = {};

/**
 * 返回一个deviceready触发之后才会解析的promise，
 * 如果deviceready已经触发，则返回Promise.resolve()
 */
function waitForDeviceReady() {
    if (!deviceready) {
        return new Promise(res => {
            queue.push(res);
        });
    } else {
        return Promise.resolve();
    }
}

/**
 * 检查key是否已配置在src/common/const/localStorageKey.js
 * @param {string} key
 */
function checkKeyExisted(key) {
    const existed = keys.includes(key);
    if (!existed) console.warn(`警告：${key}没有配置在src/common/const/localStorageKey.js`);
    return existed;
}

/**
 * 检查存储到原生的key是否符合规范
 * @param {string} key
 */
function checkNativeKey(key) {
    const res = key.indexOf('N-') === 0;
    if (!res) console.warn(`警告：${key}不符合规范，需要以‘N-’开头`);
    return res;
}

function checkKeyAndOption({ key, option }) {
    if (typeof key !== 'undefined') {
        checkKeyExisted(key);
        if (typeof key !== 'string') throw new Error(`key${key}必须为string类型`);
    }
    if (option) {
        const { expires, namespace } = option;
        if (expires) {
            if (typeof expires !== 'number' && !(expires instanceof Date)) {
                throw new Error(`expires必须为number或者Date类型`);
            }
        }
        const nameType = typeof namespace;
        if (nameType !== 'undefined' && nameType !== 'string') {
            throw new Error(`namespace必须为string或者undefined`);
        }
    }
}

/**
 * 统一处理value
 * @param {*} value
 * @param {setOption} option
 */
function createValue(value, option) {
    const { expires, bare } = option;
    if (bare) return value;
    const obj = {
        value,
    };
    if (expires) {
        const date = new Date();
        if (typeof expires !== 'number' && !(expires instanceof Date)) throw new Error('expires类型错误');
        obj.expires = typeof expires === 'number' ? Math.round(date.getTime() + expires * 1000) : expires.getTime();
    }
    return JSON.stringify(obj);
}

/**
 * 解析value
 * @param {string} text
 * @param {getOption} option
 * @returns {{value:*,expires:boolean?}}
 */
function analysisValue(text, option) {
    const { bare } = option;
    const res = {
        value: null,
    };
    if (bare) {
        res.value = text;
        return res;
    }
    if (!text) return res;
    try {
        const json = JSON.parse(text);
        if (json.expires && json.expires < new Date().getTime()) {
            res.expires = true;
            return res;
        }
        res.value = json.value;
    } catch (error) {
        res.value = text;
    }
    return res;
}

const storage = {
    /**
     * 存储localStorage，value无需限定为string类型，取值时会自动转换为原类型（基本类型）
     * @param {string} key
     * @param {*} value
     * @param {setOption} [option]
     */
    set(key, value, option = defaultOption) {
        checkKeyAndOption({ key, option });
        checkKeyExisted(key);
        return localStorage.setItem(key, createValue(value, option));
    },
    /**
     * 获取，如果没有或者已过期，返回null
     * @param {string} key
     * @param {getOption} [option]
     */
    get(key, option = defaultGetOption) {
        checkKeyAndOption({ key });
        checkKeyExisted(key);
        const text = localStorage.getItem(key);
        const { value, expires } = analysisValue(text, option);
        if (expires) storage.remove(key);
        return value;
    },
    remove(key) {
        checkKeyAndOption({ key });
        return localStorage.removeItem(key);
    },
    clear() {
        return localStorage.clear();
    },
    /**
     * 调用zzc.call('base_localStorageSetItem'，存储到app，value无需限定为string类型，取值时会自动转换为原类型（基本类型）
     * @param {string} key
     * @param {*} value
     * @param {setOption} [option]
     */
    async setNative(key, value, option = defaultOption) {
        if (!value && env.isIos) {
            console.error('ios环境下set空值会导致闪退！请使用removeNative方法');
            window.monitor?.report({
                level: 'error',
                msg: 'ios环境下set空值会导致闪退！请使用removeNative方法：' + new Error().stack,
            });

            return;
        }
        checkKeyAndOption({ key, option });
        if (!available) return;
        await waitForDeviceReady();
        const { namespace } = option;
        checkKeyExisted(key);
        checkNativeKey(key);
        await zzcCall('base_localStorageSetItem', {
            key,
            value: createValue(value, option),
            namespace,
        });
    },
    async removeNative(key, option = defaultGetOption) {
        checkKeyAndOption({ key, option });
        if (!available) return;
        await waitForDeviceReady();
        const { namespace } = option;
        const data = { key };
        if (namespace) data.namespace = namespace;
        await zzcCall('base_localStorageRemoveItem', data);
    },
    /**
     * 获取app存储内的内容
     * @param {string} key
     * @param {getOption} [option]
     */
    async getNative(key, option = defaultGetOption) {
        checkKeyAndOption({ key, option });
        checkKeyExisted(key);
        if (!available) return null;
        await waitForDeviceReady();
        const { namespace } = option;
        const data = { key };
        if (namespace) data.namespace = namespace;
        const response = await zzcCall('base_localStorageGetItem', data);
        const { value, expires } = analysisValue(response.data, option);
        if (expires) storage.removeNative(key, option);
        return value;
    },
    /**
     * 清除原生端数据
     * @param {getOption} option
     */
    async cleanNative(option = defaultGetOption) {
        checkKeyAndOption({ option });
        if (!available) return;
        await waitForDeviceReady();
        const { namespace } = option;
        const data = {};
        if (namespace) data.namespace = namespace;
        await zzcCall('base_localStorageClean', data);
    },
    async getNativeAll(option = defaultGetOption) {
        checkKeyAndOption({ option });
        if (!available) return;
        await waitForDeviceReady();
        const { namespace } = option;
        const data = {};
        if (namespace) data.namespace = namespace;
        const response = await zzcCall('base_localStorageAllItems', data);
        try {
            const data = JSON.parse(response.data);
            return data;
        } catch (error) {
            console.error(error);
            return response.data;
        }
    },
    /**
     * 同时设置localStorage和app的zzc.call('base_localStorageGetItem'，value无需限定为string类型，取值时会自动转换为原类型（基本类型）
     * @param {string} key
     * @param {*} value
     * @param {setOption} [option]
     */
    async setBoth(key, value, option = defaultOption) {
        storage.set(key, value, option);
        await storage.setNative(key, value, option);
    },
    /**
     * 同时获取localStorage和app的存储结果，app结果优先级更高
     * @param {string} key
     * @param {getOption} [option]
     */
    async getBoth(key, option = defaultGetOption) {
        const nativeRes = await storage.getNative(key, option);
        const lsRes = storage.get(key, option);
        return nativeRes === null ? lsRes : nativeRes;
    },
    /**
     * 同时移除指定的key的localStorage和app的存储
     * @param {string} key
     * @param {setOption} [option]
     */
    async removeBoth(key, option = defaultGetOption) {
        storage.remove(key);
        await storage.removeNative(key, option);
    },
    /**
     * 同时清除localStorage和app的存储，app结果优先级更高
     * @param {string} key
     * @param {setOption} [option]
     */
    async clearBoth(option = defaultGetOption) {
        storage.clear();
        await storage.cleanNative(option);
    },
    keys: keyConfig,
};

/**
 * 测试用例
 */
// async function test() {
//     window.storage = storage;
//     const value = 123;
//     storage.set(keyConfig.testKey, value);
//     if (storage.get(keyConfig.testKey) !== value)
//         console.error('storage.js测试==========================get或者set错误');
//     storage.set(keyConfig.testKey, value);
//     console.warn(`storage.js测试==========================设置值=${storage.get(keyConfig.testKey)}`);
//     storage.remove(keyConfig.testKey);
//     if (storage.get(keyConfig.testKey) !== null) console.error('storage.js测试==========================reomove错误');

//     // 原始数据测试
//     const value3 = '{a:123}';
//     storage.set(keyConfig.testKey, value3, { bare: true });
//     const v10 = storage.get(keyConfig.testKey, { bare: true });
//     if (v10 !== localStorage.getItem(keyConfig.testKey) || v10 !== value3) console.error('取原始数据失败');
//     // 过期测试
//     storage.set(keyConfig.testKey, value, { expires: 3 });
//     if (storage.get(keyConfig.testKey) !== value)
//         console.error('storage.js测试==========================get或者set过期错误');
//     setTimeout(() => {
//         if (storage.get(keyConfig.testKey) !== null)
//             console.error('storage.js测试==========================get或者set未过期错误');
//     }, 3500);

//     // app测试
//     await storage.setNative(keyConfig.testKey, value);
//     const v = await storage.getNative(keyConfig.testKey);
//     if (v !== value) console.error('storage.js测试==========================setNative或者getNative错误');
//     await storage.setNative(keyConfig.testKey, value);
//     const v1 = await storage.getNative(keyConfig.testKey);
//     console.warn(`storage.js测试==========================设置值=${v1}`);
//     await storage.removeNative(keyConfig.testKey);
//     const v2 = await storage.getNative(keyConfig.testKey);
//     if (v2 !== null) console.error('storage.js测试==========================removeNative错误');

//     // 原始数据测试
//     await storage.setNative(keyConfig.testKey, value3, { bare: true });
//     const v11 = await storage.getNative(keyConfig.testKey, { bare: true });
//     const { data: v12 } = await zzcCall('base_localStorageGetItem', { key: keyConfig.testKey });
//     if (v11 !== v12 || v11 !== value3) console.error('取原始数据失败');

//     // app命名空间测试
//     const value2 = 'abc';
//     const opt = { namespace: 'test1' };
//     await storage.setNative(keyConfig.testKey, value);
//     await storage.setNative(keyConfig.testKey, value2, opt);
//     const v5 = await storage.getNative(keyConfig.testKey);
//     const v6 = await storage.getNative(keyConfig.testKey, opt);
//     if (!(v5 === value && v6 === value2)) console.error('storage.js测试==========================namespace失败');

//     // 过期测试
//     await storage.setNative(keyConfig.testKey, value, { expires: 5 });
//     const v3 = await storage.getNative(keyConfig.testKey);
//     if (v3 !== value) console.error('storage.js测试==========================native get或者set过期错误');
//     setTimeout(async () => {
//         const v405 = await storage.getNative(keyConfig.testKey, { bare: true });
//         const v4 = await storage.getNative(keyConfig.testKey);
//         if (v4 !== null) console.error('storage.js测试==========================native get或者set未过期错误');
//         if (typeof v405 !== 'string') console.error('取原始数据失败，结果为', v405);
//         console.log('取得原始数据为：', v405);
//         console.warn('storage.js测试==========================storage.js测试完成======================');
//     }, 5500);
// }
// test();

export default storage;
